import { tokenObject } from "model/tokenObj";
import { SessionUser } from "model/userSession";

const SESSION_TOKEN_KEY = 'argon_session_token';
const SESSION_USER_KEY = 'argon_session_user';
const LEGACY_ACCESS_KEY = 'argon_access_token';
const LEGACY_REFRESH_KEY = 'argon_refresh_token';
const LEGACY_HEARTBEAT_KEY = 'argon_pwa_heartbeat';

export class LocalStorageService {
    private get storage(): Storage | null {
        return typeof window !== 'undefined' ? window.localStorage : null;
    }

    constructor() {
        this.migrateFromSessionStorage();
        this.wipeLegacyKeys();
    }

    /** One-time move of session token from sessionStorage → localStorage. */
    private migrateFromSessionStorage(): void {
        if (typeof window === 'undefined') {
            return;
        }
        try {
            const ss = window.sessionStorage;
            const ls = window.localStorage;
            const token = ss.getItem(SESSION_TOKEN_KEY);
            if (token && !ls.getItem(SESSION_TOKEN_KEY)) {
                ls.setItem(SESSION_TOKEN_KEY, token);
            }
            const user = ss.getItem(SESSION_USER_KEY);
            if (user && !ls.getItem(SESSION_USER_KEY)) {
                ls.setItem(SESSION_USER_KEY, user);
            }
            ss.removeItem(SESSION_TOKEN_KEY);
            ss.removeItem(SESSION_USER_KEY);
        } catch {
            /* private mode */
        }
    }

    /** Drop old access/refresh keys and abandoned PWA experiment keys. */
    private wipeLegacyKeys(): void {
        if (typeof window === 'undefined') {
            return;
        }
        try {
            window.localStorage.removeItem(LEGACY_ACCESS_KEY);
            window.localStorage.removeItem(LEGACY_REFRESH_KEY);
            window.localStorage.removeItem(LEGACY_HEARTBEAT_KEY);
            window.sessionStorage.removeItem(LEGACY_ACCESS_KEY);
            window.sessionStorage.removeItem(LEGACY_REFRESH_KEY);
        } catch {
            /* private mode */
        }
    }

    setSessionToken(token: string | null): void {
        const s = this.storage;
        if (!s) return;
        if (token) s.setItem(SESSION_TOKEN_KEY, token);
        else s.removeItem(SESSION_TOKEN_KEY);
    }

    getSessionToken(): tokenObject {
        const s = this.storage;
        if (!s) {
            throw new Error("LocalStorage Error");
        }
        const token = s.getItem(SESSION_TOKEN_KEY);
        return new tokenObject(token || "");
    }

    saveSessionToken(token: string | null): void {
        this.setSessionToken(token);
    }

    saveSessionUser(user: SessionUser | null): void {
        const s = this.storage;
        if (!s) return;
        if (!user) {
            s.removeItem(SESSION_USER_KEY);
            return;
        }
        s.setItem(SESSION_USER_KEY, JSON.stringify(user));
    }

    getSessionUser(): SessionUser | null {
        const s = this.storage;
        if (!s) return null;
        const raw = s.getItem(SESSION_USER_KEY);
        if (!raw) return null;
        try {
            return JSON.parse(raw) as SessionUser;
        } catch {
            return null;
        }
    }

    clearTokens(): void {
        if (typeof window === 'undefined') {
            return;
        }
        try {
            window.localStorage.removeItem(SESSION_TOKEN_KEY);
            window.localStorage.removeItem(SESSION_USER_KEY);
            window.sessionStorage.removeItem(SESSION_TOKEN_KEY);
            window.sessionStorage.removeItem(SESSION_USER_KEY);
        } catch {
            /* private mode */
        }
        this.wipeLegacyKeys();
    }

    hasToken(): boolean {
        try {
            return !!this.getSessionToken().getFullString();
        } catch {
            return false;
        }
    }
}

const localStorageService = new LocalStorageService();
export default localStorageService;
